import { $createHorizontalRuleNode } from "@lexical/extension";
import { $generateNodesFromDOM } from "@lexical/html";
import { $createLinkNode, $isLinkNode } from "@lexical/link";
import { $createListItemNode, $createListNode } from "@lexical/list";
import {
  $createHeadingNode,
  $createQuoteNode,
  HeadingTagType,
} from "@lexical/rich-text";
import {
  $createTableCellNode,
  $createTableNode,
  $createTableRowNode,
} from "@lexical/table";
import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isElementNode,
  $isTextNode,
  LexicalEditor,
  LexicalNode,
} from "lexical";

import {
  TStoryContentElementType,
  TStoryContentElementTypeBase,
  TStoryContentElementTypeHeading,
  TStoryContentElementTypeImage,
  TStoryContentElementTypeList,
  TStoryContentElementTypeOEmbedResponse,
  TStoryContentElementTypeQuote,
  TStoryContentElementTypeRawHtml,
  TStoryContentElementTypeTable,
  TStoryContentElementTypeText,
} from "@dl/declarations";
import { $createBlurbNode, $isBlurbNode } from "../nodes/CustomBlurbNode";
import { $createChartNode, $isChartNode } from "../nodes/CustomChartNode";
import { $createETFNode, $isETFNode } from "../nodes/CustomETFNode";
import { $createNowReadNode, $isNowReadNode } from "../nodes/CustomNowReadNode";
import {
  $createRoundTableIntervieweeNode,
  $isRoundTableIntervieweeNode,
} from "../nodes/CustomRoundTableIntervieweeNode";
import {
  $createSectionInfoNode,
  $isSectionInfoNode,
} from "../nodes/CustomSectionInfoNode";
import { $createImageNode, $isImageNode, ImageNode } from "../nodes/ImageNode";
import { $createOEmbedNode, OEmbedNode } from "../nodes/OEmbedNode";

// INPUT //
interface IParse5Node {
  nodeName: string;
  tagName?: string;
  value?: string;
  attrs?: { name: string; value: string }[];
  childNodes?: IParse5Node[];
}

const convertFormattedText = (node: IParse5Node): LexicalNode | null => {
  const text = node.childNodes?.[0]?.value ?? "";
  const textNode = $createTextNode(text);

  if (node.tagName === "strong" || node.tagName === "b") {
    textNode.setFormat("bold");
  }
  if (node.tagName === "em" || node.tagName === "i") {
    textNode.setFormat("italic");
  }
  if (node.tagName === "u") {
    textNode.setFormat("underline");
  }

  return textNode;
};

const convertNodeToLexical = (node: IParse5Node): LexicalNode | null => {
  if (node.nodeName === "#text") {
    const text = node.value;
    return text ? $createTextNode(text) : null;
  }

  if (!node.tagName) return null;

  const tag = node.tagName.toLowerCase();
  let lexicalNode: LexicalNode | null = null;

  switch (tag) {
    case "p": {
      lexicalNode = $createParagraphNode();
      break;
    }

    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "h5":
    case "h6": {
      lexicalNode = $createHeadingNode(tag);
      break;
    }

    case "strong":
    case "b":
    case "em":
    case "i":
    case "u": {
      return convertFormattedText(node);
    }

    case "a": {
      const attrs = node.attrs || [];
      const href = attrs.find((attr) => attr.name === "href")?.value || "/";
      const target =
        attrs.find((attr) => attr.name === "target")?.value || "_blank";
      const rel = attrs.find((attr) => attr.name === "rel")?.value || null;

      lexicalNode = $createLinkNode(href, {
        target,
        rel,
      });
      break;
    }

    case "br": {
      lexicalNode = $createLineBreakNode();
      break;
    }

    case "hr": {
      lexicalNode = $createHorizontalRuleNode();
      break;
    }

    case "div":
    case "span": {
      // For div and span, create a container and process children
      lexicalNode = $createParagraphNode();
      break;
    }

    default: {
      // Fallback to paragraph for unknown tags
      lexicalNode = $createParagraphNode();
    }
  }

  // Recursively process child nodes
  if (node.childNodes && node.childNodes.length > 0) {
    for (const child of node.childNodes) {
      const childLexical = convertNodeToLexical(child);
      if (childLexical && $isElementNode(lexicalNode)) {
        lexicalNode.append(childLexical);
      }
    }
  }

  return lexicalNode;
};

const parseHtmlToLexicalNodes = (
  editor: LexicalEditor,
  htmlContent: string,
): LexicalNode[] => {
  // OPTION 1
  // In the browser you can use the native DOMParser API to parse the HTML string.
  const parser = new DOMParser();
  const dom = parser.parseFromString(htmlContent, "text/html");

  // Once you have the DOM instance it's easy to generate LexicalNodes.
  const nodes = $generateNodesFromDOM(editor, dom);
  return nodes;

  // OPTION 2
  // const nodes: LexicalNode[] = [];

  // const parsed = parse(htmlContent);

  // Recursive function to process nodes at any level
  // const processNodesRecursively = (nodeList: IParse5Node[]): LexicalNode[] => {
  //   const result: LexicalNode[] = [];

  //   for (const node of nodeList) {
  //     const lexicalNode = convertNodeToLexical(node);
  //     if (lexicalNode) {
  //       result.push(lexicalNode);
  //     }
  //   }

  //   return result;
  // };

  // // Try to find the body first, then html, then process all child nodes
  // const html: IParse5Node | undefined = parsed.childNodes.find(
  //   (n: any) => n.tagName === "html"
  // );
  // const body: IParse5Node | undefined = html?.childNodes?.find(
  //   (n: any) => n.tagName === "body"
  // );

  // // Process nodes from body if available, otherwise from html, otherwise from parsed root
  // const sourceNodes =
  //   body?.childNodes || html?.childNodes || parsed.childNodes || [];

  // const processedNodes = processNodesRecursively(sourceNodes);
  // nodes.push(...processedNodes);

  // // If no content was parsed, return the original content as a text node
  // if (nodes.length === 0) {
  //   nodes.push($createTextNode(htmlContent));
  // }

  // return nodes;
};

const parseTextTypeElement = (
  editor: LexicalEditor,
  element: TStoryContentElementTypeText,
) => {
  const p = $createParagraphNode();
  const nodes = parseHtmlToLexicalNodes(editor, element.content);
  nodes.forEach((node) => p.append(node));
  return p;
};

const parseHeadingTypeElement = (
  editor: LexicalEditor,
  element: TStoryContentElementTypeHeading,
) => {
  const heading = $createHeadingNode(
    ("h" + (element.level || 2)) as HeadingTagType,
  );
  const nodes = parseHtmlToLexicalNodes(editor, element.content);
  nodes.forEach((node) => heading.append(node));
  return heading;
};

const parseListTypeElement = (
  editor: LexicalEditor,
  element: TStoryContentElementTypeList,
) => {
  const listNode = $createListNode(
    element.list_type === "unordered" ? "bullet" : "number",
    element.level || 1,
  );

  element.items.forEach((item) => {
    const listItemNode = $createListItemNode();
    const nodes = parseHtmlToLexicalNodes(editor, item.content);
    nodes.forEach((node) => listItemNode.append(node));
    listNode.append(listItemNode);
  });

  return listNode;
};

const parseQuoteTypeElement = (
  editor: LexicalEditor,
  element: TStoryContentElementTypeQuote,
) => {
  const quoteNode = $createQuoteNode();

  element.content_elements.forEach((element) => {
    if (["text", "header"].includes(element.type) && "content" in element) {
      if (element.type === "text") {
        quoteNode.append(parseTextTypeElement(editor, element));
      } else if (element.type === "header") {
        quoteNode.append(parseHeadingTypeElement(editor, element));
      }
    } else if (element.type === "list") {
      quoteNode.append(parseListTypeElement(editor, element));
    }
  });

  return quoteNode;
};

const parseTableTypeElement = (
  editor: LexicalEditor,
  element: TStoryContentElementTypeTable,
) => {
  const tableNode = $createTableNode();
  tableNode.setStyle("width: 100%;");

  const rowNode = $createTableRowNode();
  element.header.forEach((element) => {
    const cellNode = $createTableCellNode();
    const nodes = parseHtmlToLexicalNodes(editor, element.content);
    nodes.forEach((node) => cellNode.append(node));
    rowNode.append(cellNode);
  });
  tableNode.append(rowNode);

  const rowNodes = element.rows.map((row) => {
    const rowNode = $createTableRowNode();
    row.forEach((cell) => {
      const cellNode = $createTableCellNode();
      const nodes = parseHtmlToLexicalNodes(editor, cell.content);
      nodes.forEach((node) => cellNode.append(node));
      rowNode.append(cellNode);
    });
    return rowNode;
  });

  rowNodes.forEach((rowNode) => tableNode.append(rowNode));
  return tableNode;
};

const parseUnknownTypeElement = (element: TStoryContentElementType) => {
  console.warn(
    `[Unsupported custom_embed subtype: ${element.subtype}] in contentElementsToLexical`,
    element,
  );

  const unknown = $createParagraphNode();
  unknown.append(
    $createTextNode(
      `[Unsupported element: ${element.type} - ${element.subtype}]`,
    ),
  );

  return unknown;
};

/**
 * Converts an array of content elements from a story into Lexical nodes.
 * This function processes each content element type and appends the corresponding Lexical nodes to the editor
 */
export const contentElementsToLexical = (
  editor: LexicalEditor,
  content_elements: Array<TStoryContentElementType>,
  options: { clearExisting?: boolean } = { clearExisting: true },
): void => {
  editor.update(() => {
    const root = $getRoot();

    if (options.clearExisting) {
      root.clear();
    }

    (content_elements || []).forEach((element) => {
      switch (element.type) {
        case "text": {
          root.append(parseTextTypeElement(editor, element));
          break;
        }

        case "header": {
          root.append(parseHeadingTypeElement(editor, element));
          break;
        }

        case "divider": {
          const dividerNode = $createHorizontalRuleNode(); // or a custom HR node
          root.append(dividerNode);
          break;
        }

        case "list": {
          root.append(parseListTypeElement(editor, element));
          break;
        }

        case "quote": {
          root.append(parseQuoteTypeElement(editor, element));
          break;
        }

        case "interstitial_link": {
          break;
        }

        case "image": {
          const imageNode = $createImageNode({
            ...element,
            src: element.url,
          });

          root.append(imageNode);
          break;
        }

        case "table": {
          root.append(parseTableTypeElement(editor, element));
          break;
        }

        case "correction": {
          break;
        }

        case "copyright": {
          const copyrightNode = $createParagraphNode();
          const textNode = $createTextNode(element.content || "");
          copyrightNode.append(textNode);
          root.append(copyrightNode);
          break;
        }

        case "raw_html": {
          if (element.content) {
            const rawHtmlNode = $createOEmbedNode(
              "",
              element.content,
              "generic",
            );
            root.append(rawHtmlNode);
          }
          break;
        }

        case "oembed_response": {
          if (element.raw_oembed.html || element.raw_oembed.url) {
            const oembedNode = $createOEmbedNode(
              element.raw_oembed.url || "",
              element.raw_oembed.html || "",
              element.subtype || "generic",
            );
            root.append(oembedNode);
          }
          break;
        }

        case "custom_embed": {
          switch (element.subtype) {
            case "section-info": {
              const sectionInfoNode = $createSectionInfoNode(element);
              root.append(sectionInfoNode);
              break;
            }

            case "embed-chart": {
              const chartNode = $createChartNode(element);
              root.append(chartNode);
              break;
            }

            case "embed-etf": {
              const etfNode = $createETFNode(element);
              root.append(etfNode);
              break;
            }

            case "now-read": {
              const nowReadNode = $createNowReadNode(element);
              root.append(nowReadNode);
              break;
            }

            case "blurb": {
              const blurbNode = $createBlurbNode(element);
              root.append(blurbNode);
              break;
            }

            case "roundtable-interviewees": {
              const roundTableIntervieweeNode =
                $createRoundTableIntervieweeNode(element);
              root.append(roundTableIntervieweeNode);
              break;
            }

            default: {
              root.append(parseUnknownTypeElement(element));
              break;
            }
          }
          break;
        }

        default: {
          root.append(parseUnknownTypeElement(element));
          break;
        }
      }
    });

    if ((content_elements || []).length > 0) {
      root.append($createParagraphNode());
    }

    // setTimeout(() => {
    //   console.log("lexicalToContentElements", lexicalToContentElements(editor));
    // }, 1000);
  });
};

// OUTPUT //

/**
 * Converts HTML-like content within a node to a plain HTML string
 */
const convertNodeChildrenToHtml = (node: LexicalNode): string => {
  if ($isTextNode(node)) {
    let text = node.getTextContent();

    if (node.hasFormat("bold")) {
      text = `<strong>${text}</strong>`;
    }
    if (node.hasFormat("italic")) {
      text = `<em>${text}</em>`;
    }
    if (node.hasFormat("underline")) {
      text = `<u>${text}</u>`;
    }

    return text;
  }

  if ($isLinkNode(node)) {
    const url = node.getURL();
    const target = node.getTarget();
    const rel = node.getRel();
    let linkContent = "";

    if ($isElementNode(node)) {
      node.getChildren().forEach((childNode) => {
        linkContent += convertNodeChildrenToHtml(childNode);
      });
    }

    if (!linkContent) {
      linkContent = node.getTextContent();
    }

    let linkHtml = `<a href="${url}"`;
    if (target) linkHtml += ` target="${target}"`;
    if (rel) linkHtml += ` rel="${rel}"`;
    linkHtml += `>${linkContent}</a>`;

    return linkHtml;
  }

  if (node.getType() === "linebreak") {
    return "<br>";
  }

  if (node.getType() === "divider") {
    return "<hr />";
  }

  let htmlContent = "";

  if ($isElementNode(node)) {
    const children = node.getChildren();

    children.forEach((childNode) => {
      htmlContent += convertNodeChildrenToHtml(childNode);
    });
  }

  return htmlContent;
};

const extractContentElementsFromParagraphNode = (
  node: LexicalNode,
  index: number,
): TStoryContentElementType[] => {
  if (!$isElementNode(node)) return [];

  const paragraphElements: TStoryContentElementType[] = [];
  let htmlBuffer = "";
  let textChunkIndex = 0;

  // Emit one text element for the accumulated non-image siblings.
  // Called before pushing an image and once at the end.
  const flushTextBuffer = () => {
    const trimmedContent = (htmlBuffer || "").trim();
    if (!trimmedContent) {
      htmlBuffer = "";
      return;
    }

    paragraphElements.push({
      _id: `paragraph-${index}-${textChunkIndex}`,
      type: "text",
      content: trimmedContent,
    } as TStoryContentElementTypeBase & TStoryContentElementTypeText);
    textChunkIndex += 1;
    htmlBuffer = "";
  };

  node.getChildren().forEach((childNode, childIndex) => {
    if ($isImageNode(childNode)) {
      flushTextBuffer();
      const imageNode = childNode as any; // ImageNode
      paragraphElements.push({
        _id: `image-${index}-${childIndex}`,
        type: "image",
        url: imageNode.__src || "",
        link: imageNode.__link || "",
        alt_text: imageNode.__alt_text || "",
        headline: imageNode.__headline || "",
        credit: imageNode.__credit || "",
        copyright: imageNode.__copyright || "",
        article_caption: imageNode.__article_caption || "",
        alignment: "center",
        keywords: [],
      } as TStoryContentElementTypeBase & TStoryContentElementTypeImage);
      return;
    }

    htmlBuffer += convertNodeChildrenToHtml(childNode);
  });

  flushTextBuffer();
  return paragraphElements;
};

/**
 * Recursively converts a Lexical node to the appropriate content element
 */
const convertLexicalNodeToContentElement = (
  node: LexicalNode,
  index: number,
): TStoryContentElementType | TStoryContentElementType[] | null => {
  const nodeType = node.getType();

  switch (nodeType) {
    case "paragraph": {
      return extractContentElementsFromParagraphNode(
        node,
        index,
      );
    }

    case "heading": {
      const headingNode = node as any; // HeadingNode
      const htmlContent = convertNodeChildrenToHtml(node);

      let level = parseInt(headingNode.getTag().replace("h", "")) || 2;

      if (level < 2) {
        level = 2; // Default to h2 if out of range
      }

      if (level > 6) {
        level = 6; // Cap at h6
      }

      return {
        _id: `heading-${index}`,
        type: "header",
        level: level as 2 | 3 | 4 | 5 | 6,
        content: htmlContent,
        additional_properties: {
          link: "",
        },
      } as TStoryContentElementTypeBase & TStoryContentElementTypeHeading;
    }

    case "quote": {
      const quoteNode = node as any; // QuoteNode
      const contentElements: TStoryContentElementType[] = [];

      if ($isElementNode(quoteNode)) {
        quoteNode.getChildren().forEach((childNode: LexicalNode, index) => {
          if (childNode.__type === "text") {
            contentElements.push({
              _id: `text-${index}`,
              type: "text",
              content: childNode.getTextContent(),
            } as TStoryContentElementTypeBase & TStoryContentElementTypeText);
          } else {
            const childElement = convertLexicalNodeToContentElement(
              childNode,
              index,
            );

            if (childElement) {
              if (Array.isArray(childElement)) {
                contentElements.push(...childElement);
              } else {
                contentElements.push(childElement);
              }
            }
          }
        });
      }

      return {
        _id: `quote-${index}`,
        type: "quote",
        content_elements: contentElements,
      } as TStoryContentElementTypeBase & TStoryContentElementTypeQuote;
    }

    case "list": {
      const listNode = node as any; // ListNode
      const listType = listNode.getListType();
      const level = listNode.getStart() || 1;
      const items: { content: string }[] = [];

      if ($isElementNode(listNode)) {
        listNode.getChildren().forEach((itemNode: LexicalNode) => {
          if (itemNode.getType() === "listitem") {
            const itemContent = convertNodeChildrenToHtml(itemNode);
            if (itemContent.trim()) {
              items.push({ content: itemContent });
            }
          }
        });
      }

      return {
        _id: `list-${index}`,
        type: "list",
        list_type: listType === "bullet" ? "unordered" : "ordered",
        level: level,
        items: items,
      } as TStoryContentElementTypeBase & TStoryContentElementTypeList;
    }

    case "horizontalrule": {
      return {
        _id: `divider-${index}`,
        type: "divider",
      } as TStoryContentElementTypeBase & TStoryContentElementType;
    }

    case "image": {
      const imageNode = node as ImageNode; // ImageNode

      const element = {
        _id: `image-${index}`,
        type: "image",
        url: imageNode.__src || "",
        link: imageNode.__link || "",
        alt_text: imageNode.__alt_text || "",
        headline: imageNode.__headline || "",
        credit: imageNode.__credit || "",
        copyright: imageNode.__copyright || "",
        article_caption: imageNode.__article_caption || "",
        alignment: "center",
        keywords: [],
      } as TStoryContentElementTypeBase & TStoryContentElementTypeImage;

      return element;
    }

    case "table": {
      const tableNode = node as any; // TableNode
      const header: { content: string }[] = [];
      const rows: { content: string }[][] = [];

      if ($isElementNode(tableNode)) {
        const tableRows = tableNode.getChildren();

        // Assume first row is header (this might need adjustment based on your table structure)
        if (tableRows.length > 0) {
          const firstRow = tableRows[0];
          if ($isElementNode(firstRow)) {
            firstRow.getChildren().forEach((cellNode: LexicalNode) => {
              const cellContent = convertNodeChildrenToHtml(cellNode);
              header.push({ content: cellContent });
            });
          }

          // Process remaining rows
          for (let i = 1; i < tableRows.length; i++) {
            const row = tableRows[i];
            if ($isElementNode(row)) {
              const rowCells: { content: string }[] = [];
              row.getChildren().forEach((cellNode: LexicalNode) => {
                const cellContent = convertNodeChildrenToHtml(cellNode);
                rowCells.push({ content: cellContent });
              });
              rows.push(rowCells);
            }
          }
        }
      }

      return {
        _id: `table-${index}`,
        type: "table",
        header: header,
        rows: rows,
      } as TStoryContentElementTypeBase & TStoryContentElementTypeTable;
    }

    case "raw_html": {
      const oembedNode = node as OEmbedNode; // OEmbedNode
      const html = oembedNode.__html;

      return {
        _id: `oembed-${index}`,
        type: "raw_html",
        content: html,
      } as TStoryContentElementTypeBase & TStoryContentElementTypeRawHtml;
    }

    case "oembed_response": {
      const oembedNode = node as OEmbedNode; // OEmbedNode
      const html = oembedNode.__html;
      const url = oembedNode.__url;
      const subtype = oembedNode.__subtype;

      return {
        _id: `oembed-${index}`,
        type: "oembed_response",
        subtype: subtype,
        raw_oembed: {
          html: html,
          url: url,
        },
      } as TStoryContentElementTypeBase &
        TStoryContentElementTypeOEmbedResponse;
    }

    default: {
      // Handle custom nodes
      if (
        $isRoundTableIntervieweeNode(node) ||
        $isSectionInfoNode(node) ||
        $isNowReadNode(node) ||
        $isChartNode(node) ||
        $isBlurbNode(node) ||
        $isETFNode(node)
      ) {
        const config = (node as any).getConfig();
        return {
          _id: `${nodeType}-${index}`,
          ...config,
        };
      }

      // For unknown node types, try to extract as text
      if ($isElementNode(node)) {
        const htmlContent = convertNodeChildrenToHtml(node);
        if (htmlContent.trim()) {
          return {
            _id: `unknown-${index}`,
            type: "text",
            content: htmlContent,
          } as TStoryContentElementTypeBase & TStoryContentElementTypeText;
        }
      }

      return null;
    }
  }
};

/**
 * Converts the current Lexical editor state to an array of content elements.
 * This function recursively processes each node and returns an array of content elements,
 * serving as the opposite of contentElementsToLexical.
 */
export const lexicalToContentElements = (
  editor: LexicalEditor,
): TStoryContentElementType[] => {
  const editorState = editor.getEditorState();
  const contentElements: TStoryContentElementType[] = [];

  editorState.read(() => {
    const root = $getRoot();

    root.getChildren().forEach((node, index) => {
      const contentElement = convertLexicalNodeToContentElement(node, index);
      if (contentElement) {
        if (Array.isArray(contentElement)) {
          contentElements.push(...contentElement);
        } else {
          contentElements.push(contentElement);
        }
      }
    });
  });

  return contentElements.filter(
    ce => !(ce.type === 'text' && ce.content === '<br />')
  );
};
